/**
 * FAQ page behaviour for sections/arvana-help-center.liquid. The section renders every question
 * with search, categories and feedback `hidden`, so it stays complete without JavaScript.
 * - Search filters questions by their question and answer text, across all categories
 * - Category buttons show one group at a time ("All" shows every group)
 * - "Was this helpful?" Yes / No, then a thank-you note
 * - A `#faq-<handle>` link opens that question
 * Analytics go through the `arvana:track` event, which assets/arvana-analytics.js publishes:
 * arvana_faq_open, arvana_faq_helpful and arvana_faq_search (debounced, the search term).
 */

/**
 * @param {string} name
 * @param {Record<string, unknown>} data
 */
function track(name, data) {
  document.dispatchEvent(new CustomEvent('arvana:track', { detail: { name, data } }));
}

/** @param {string} text */
const normalize = (text) => text.toLowerCase().replace(/\s+/g, ' ').trim();

class ArvanaHelpCenter extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;
  /** @type {number | undefined} */
  #searchTimer;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.items = /** @type {HTMLDetailsElement[]} */ ([...this.querySelectorAll('[data-item]')]);
    this.groups = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-group]')]);
    this.buttons = /** @type {HTMLButtonElement[]} */ ([...this.querySelectorAll('[data-category]')].filter(
      (element) => element instanceof HTMLButtonElement
    ));
    this.input = this.querySelector('[data-search-input]');
    this.noResults = this.querySelector('[data-no-results]');

    this.querySelector('[data-search]')?.removeAttribute('hidden');
    this.querySelector('[data-categories]')?.removeAttribute('hidden');
    this.querySelectorAll('[data-feedback]').forEach((element) => element.removeAttribute('hidden'));

    for (const button of this.buttons) {
      button.addEventListener('click', () => this.#selectCategory(button.dataset.category ?? ''), { signal });
    }

    this.input?.addEventListener('input', () => this.#onSearch(), { signal });

    for (const item of this.items) {
      item.addEventListener(
        'toggle',
        () => {
          if (item.open) track('arvana_faq_open', this.#itemData(item));
        },
        { signal }
      );
    }

    this.addEventListener('click', (event) => this.#onFeedback(event), { signal });
    window.addEventListener('hashchange', () => this.#openFromHash(), { signal });
    this.#openFromHash();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    clearTimeout(this.#searchTimer);
  }

  /** @param {HTMLDetailsElement} item */
  #itemData(item) {
    const question = item.querySelector('.arvana-help__question-text')?.textContent ?? '';
    return { label: question.replace(/\s+/g, ' ').trim().slice(0, 100), category: item.dataset.category ?? '' };
  }

  /** @param {string} handle - Category handle, '' for all */
  #selectCategory(handle) {
    if (this.input instanceof HTMLInputElement) this.input.value = '';
    for (const button of this.buttons ?? []) {
      button.setAttribute('aria-pressed', String((button.dataset.category ?? '') === handle));
    }
    for (const item of this.items ?? []) item.hidden = false;
    for (const group of this.groups ?? []) group.hidden = handle !== '' && group.dataset.group !== handle;
    if (this.noResults instanceof HTMLElement) this.noResults.hidden = true;
  }

  #onSearch() {
    const term = this.input instanceof HTMLInputElement ? normalize(this.input.value) : '';
    for (const button of this.buttons ?? []) {
      button.setAttribute('aria-pressed', String((button.dataset.category ?? '') === ''));
    }

    let matches = 0;
    for (const item of this.items ?? []) {
      const visible = term === '' || normalize(item.textContent ?? '').includes(term);
      item.hidden = !visible;
      if (visible) matches += 1;
    }
    for (const group of this.groups ?? []) {
      group.hidden = !group.querySelector('[data-item]:not([hidden])');
    }
    if (this.noResults instanceof HTMLElement) this.noResults.hidden = matches > 0;

    clearTimeout(this.#searchTimer);
    if (term.length < 2) return;
    this.#searchTimer = window.setTimeout(() => {
      track('arvana_faq_search', { label: term.slice(0, 100), results: matches });
    }, 1000);
  }

  /** @param {MouseEvent} event */
  #onFeedback(event) {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('[data-helpful]');
    const item = button?.closest('[data-item]');
    if (!(button instanceof HTMLElement) || !(item instanceof HTMLDetailsElement)) return;

    track('arvana_faq_helpful', { ...this.#itemData(item), value: button.dataset.helpful ?? '' });

    const feedback = button.closest('[data-feedback]');
    feedback?.querySelectorAll('button, span:not([role="status"])').forEach((element) => {
      if (element instanceof HTMLElement) element.hidden = true;
    });
    const thanks = feedback?.querySelector('[role="status"]');
    if (thanks instanceof HTMLElement) thanks.hidden = false;
  }

  #openFromHash() {
    const id = decodeURIComponent(window.location.hash.slice(1));
    if (!id.startsWith('faq-')) return;
    const item = document.getElementById(id);
    if (!(item instanceof HTMLDetailsElement) || !this.contains(item)) return;
    this.#selectCategory('');
    item.open = true;
    item.scrollIntoView({ block: 'start' });
  }
}

if (!customElements.get('arvana-help-center')) {
  customElements.define('arvana-help-center', ArvanaHelpCenter);
}
